import { useStudioTranslate } from '@repo/studio-config/studio-config-context'
import { useStudioNavigateTo } from '@repo/studio-config/studio-config-router'
import { trackPricingCtaClicked } from '@repo/studio-utils/analytics/amplitude'
import { useIsLoggedInQuery } from 'auth/hooks'
import { TRIAL_CTA_VARIANTS } from '../features'
import { resolveTrialCta } from '../trial-cta'
import type { TrialCta } from '../trial-cta'
import { usePaymentsDialog } from './use-payments-dialog'
import { useTrialCtaVariant } from './use-trial-cta-variant'

/**
 * Picks the text that sits around a trial CTA, so a card in the pricing-page arm
 * stops promising a free trial its button no longer opens.
 */
export function useTrialCtaCopy<T>(copy: { control: T; pricingPage: T }): T {
  return useTrialCtaVariant() === TRIAL_CTA_VARIANTS.pricingPage ? copy.pricingPage : copy.control
}

/**
 * The one place the pricing-page experiment exists in code: it decides the label
 * a trial CTA renders and what its click does. Callers render what it returns and
 * never learn which arm they are in.
 */
export function useTrialCta({
  controlLabel,
  tracksControlClicks = true,
}: {
  controlLabel?: string
  tracksControlClicks?: boolean
} = {}): TrialCta {
  const t = useStudioTranslate()
  const variant = useTrialCtaVariant()
  const navigateTo = useStudioNavigateTo()
  const { open } = usePaymentsDialog()
  const { data: isLoggedIn } = useIsLoggedInQuery()

  return resolveTrialCta({
    variant,
    isLoggedIn: Boolean(isLoggedIn),
    trialLabel: controlLabel ?? t('studio.components.subscription_message.cta'),
    pricingLabel: t('studio.components.subscription_message.see_pricing'),
    tracksControlClicks,
    openPaymentsDialog: open,
    navigateTo,
    trackPricingCtaClicked,
  })
}
